/**
 * Cache Recovery Utility
 *
 * Provides functions to detect, manage, and recover from stale cache issues,
 * particularly related to service worker cache and localStorage inconsistencies.
 */

import { useLogger } from 'src/utils/logger';
import { CONSTANTS } from 'src/constants';

const logger = useLogger('CacheRecovery');

/**
 * Minimum safe version - versions below this may have import.meta issues
 */
export const MIN_SAFE_VERSION = '9.3.2';

/**
 * Clears application cache and storage
 * Used when stale cache is detected
 */
export async function clearAppCache(): Promise<void> {
  logger.info('Clearing application cache...');

  try {
    // Clear localStorage
    const criticalKeys = [CONSTANTS.app_version]; // Keep critical keys
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !criticalKeys.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
      logger.debug(`Cleared localStorage key: ${key}`);
    });

    // Clear sessionStorage
    sessionStorage.clear();
    logger.debug('Cleared sessionStorage');

    // Clear service worker cache
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      logger.debug(`Cleared ${cacheNames.length} service worker caches`);
    }

    // Clear IndexedDB
    const indexedDBDatabases = ['docbits_db', 'app_cache'];
    indexedDBDatabases.forEach((dbName) => {
      try {
        const req = indexedDB.deleteDatabase(dbName);
        req.onsuccess = () => logger.debug(`Cleared IndexedDB: ${dbName}`);
        req.onerror = () =>
          logger.warn(`Failed to clear IndexedDB: ${dbName}`);
      } catch (error) {
        logger.warn(`Error deleting IndexedDB ${dbName}:`, error);
      }
    });

    logger.info('Application cache cleared successfully');
  } catch (error) {
    logger.error('Error clearing application cache:', error);
  }
}

/**
 * Delete all Cache API entries (Workbox precache + runtime caches) without
 * touching localStorage / sessionStorage / IndexedDB.
 *
 * This is the narrow building block reused by the logout flow: logout has its
 * own storage-clearing logic with a keep-on-logout whitelist, so it must NOT
 * call clearAppCache() (which wipes localStorage wholesale). It only wants the
 * service-worker cache gone so the next session never serves stale cached
 * responses. The service worker stays registered and re-precaches on the next
 * navigation.
 */
export async function clearServiceWorkerCaches(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    logger.debug(`Cleared ${cacheNames.length} service worker caches`);
  } catch (error) {
    logger.error('Error clearing service worker caches:', error);
  }
}

/**
 * Compare two semantic versions
 * @returns -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map((part) => parseInt(part, 10));
  const parts2 = v2.split('.').map((part) => parseInt(part, 10));

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 < part2) return -1;
    if (part1 > part2) return 1;
  }

  return 0;
}

/**
 * Check if the cached version is stale
 * (older than minimum safe version)
 */
export function isStaleVersion(): boolean {
  try {
    const storedVersion = localStorage.getItem(CONSTANTS.app_version);
    if (!storedVersion) return false;

    const isStale =
      compareVersions(storedVersion, MIN_SAFE_VERSION) < 0;

    if (isStale) {
      logger.warn(
        `Stale version detected: ${storedVersion} < ${MIN_SAFE_VERSION}`
      );
    }

    return isStale;
  } catch (error) {
    logger.error('Error checking stale version:', error);
    return false;
  }
}

/**
 * Perform full cleanup and reload
 * Used when critical cache issues are detected
 */
export async function performFullCleanupAndReload(): Promise<void> {
  logger.warn('Performing full cache cleanup and reload...');

  try {
    // Clear cache
    await clearAppCache();

    // Update version in localStorage
    const currentVersion = process.env.VITE_APP_VERSION || '1.0.0';
    localStorage.setItem(CONSTANTS.app_version, currentVersion);

    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration) => registration.unregister())
      );
      logger.info(`Unregistered ${registrations.length} service workers`);
    }

    // Hard reload
    logger.info('Reloading application...');
    window.location.reload();
  } catch (error) {
    logger.error('Error during cleanup and reload:', error);
    // Fallback to simple reload
    window.location.reload();
  }
}

/**
 * Check for import.meta errors in the page
 * @returns true if import.meta syntax errors detected
 */
export function detectImportMetaErrors(): boolean {
  // Listen for script errors that contain 'import.meta'
  let hasError = false;

  window.addEventListener('error', (event: ErrorEvent) => {
    if (
      event.message &&
      event.message.includes('import.meta') &&
      event.message.includes('SyntaxError')
    ) {
      hasError = true;
      logger.error('import.meta syntax error detected:', event.message);
    }
  });

  return hasError;
}

/**
 * Check if service worker needs update
 * Detects mismatches between expected and actual service worker
 */
export async function checkServiceWorkerUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      // Check if service worker is stale
      if (registration.active) {
        logger.debug('Service worker is active and running');
        return false;
      }

      // Unregister stale service workers
      if (registration.installing || registration.waiting) {
        logger.warn('Stale service worker detected, unregistering');
        await registration.unregister();
        return true;
      }
    }

    return false;
  } catch (error) {
    logger.error('Error checking service worker:', error);
    return false;
  }
}

/**
 * Get current app state information for debugging
 */
export function getAppStateInfo(): {
  storedVersion: string | null;
  currentVersion: string;
  isStale: boolean;
  hasServiceWorker: boolean;
  swCount: number;
} {
  const storedVersion = localStorage.getItem(CONSTANTS.app_version);
  const currentVersion = process.env.VITE_APP_VERSION || '1.0.0';

  return {
    storedVersion,
    currentVersion,
    isStale: storedVersion ? compareVersions(storedVersion, MIN_SAFE_VERSION) < 0 : false,
    hasServiceWorker: 'serviceWorker' in navigator,
    swCount: 0, // Will be updated by caller if needed
  };
}
